import React, { useState, useEffect, MouseEvent } from "react";
import { IconButton, Popover, Slider, Stack, Tooltip } from "@mui/material";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeDownIcon from "@mui/icons-material/VolumeDown";
import VolumeMuteIcon from "@mui/icons-material/VolumeMute";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import MicOffIcon from "@mui/icons-material/MicOff";
import MicIcon from "@mui/icons-material/Mic";
import type { MutableRefObject, RefObject } from "react";

const STORAGE_KEY = "volumeControlSettings";

interface VolumeControlProps {
  remoteAudioRef: RefObject<HTMLAudioElement>;
  localStreamRef: MutableRefObject<MediaStream | null>;
  peerConnectionRef?: MutableRefObject<RTCPeerConnection | null>;
  inCall: boolean;
}

export default function VolumeControl({
  remoteAudioRef,
  localStreamRef,
  peerConnectionRef,
  inCall,
}: VolumeControlProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [volume, setVolume] = useState<number>(75);
  const [prevVolume, setPrevVolume] = useState<number>(75);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [micMuted, setMicMuted] = useState<boolean>(false);

  // 1) On mount: initialize from localStorage (or default to 75% unmuted)
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const { volume: v, isMuted: m } = JSON.parse(saved);
        setVolume(v);
        setPrevVolume(v);
        setIsMuted(m);
      } catch {
        // fallback defaults already set
      }
    }
  }, []);

  // 2) Persist volume + speaker-mute changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ volume, isMuted }));
  }, [volume, isMuted]);

  // 3) Reset micMute at start of each call
  useEffect(() => {
    setMicMuted(false);
  }, [inCall]);

  // Update remote audio element whenever volume or mute changes
  useEffect(() => {
    if (remoteAudioRef.current) {
      remoteAudioRef.current.volume = isMuted ? 0 : volume / 100;
    }
  }, [volume, isMuted, remoteAudioRef]);

  // Open/close popover
  const handleClick = (e: MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // Toggle speaker mute
  const toggleMuteViaIcon = () => {
    if (isMuted) {
      setIsMuted(false);
      setVolume(prevVolume);
    } else {
      setPrevVolume(volume);
      setIsMuted(true);
      setVolume(0);
    }
  };

  // Slider change
  const handleVolumeChange = (_: Event, newValue: number | number[]) => {
    const v = Array.isArray(newValue) ? newValue[0] : newValue;
    setVolume(v);
    const mute = v === 0;
    setIsMuted(mute);
    if (!mute) setPrevVolume(v);
  };

  // Toggle mic on/off
  const handleMicToggle = () => {
    const newMuted = !micMuted;
    setMicMuted(newMuted);

    localStreamRef.current?.getAudioTracks().forEach((t) => {
      t.enabled = !newMuted;
    });

    peerConnectionRef?.current?.getSenders().forEach((s) => {
      if (s.track?.kind === "audio") s.track.enabled = !newMuted;
    });
  };

  // Icon based on volume
  const getVolumeIcon = () => {
    if (isMuted || volume === 0) return <VolumeOffIcon />;
    if (volume >= 75) return <VolumeUpIcon />;
    if (volume >= 25) return <VolumeDownIcon />;
    return <VolumeMuteIcon />;
  };

  const open = Boolean(anchorEl);
  const id = open ? "volume-popover" : undefined;

  return (
    <>
      {/* speaker + mic buttons */}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Tooltip title={isMuted ? "Unmute Speaker" : "Mute Speaker"}>
          <IconButton onClick={handleClick} aria-describedby={id}>
            {getVolumeIcon()}
          </IconButton>
        </Tooltip>

        <Tooltip title={micMuted ? "Unmute Mic" : "Mute Mic"}>
          <IconButton onClick={handleMicToggle}>
            {micMuted ? <MicOffIcon sx={{ color: "#B31942" }} /> : <MicIcon />}
          </IconButton>
        </Tooltip>
      </Stack>

      {/* slider popover */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: {
              mt: -1,
              ml: 2,
              "&::before": { display: "none" },
            },
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ px: 2, py: 1 }}
        >
          <Tooltip title={isMuted ? "Unmute Speaker" : "Mute Speaker"}>
            <IconButton onClick={toggleMuteViaIcon}>
              {isMuted || volume === 0 ? <VolumeOffIcon /> : getVolumeIcon()}
            </IconButton>
          </Tooltip>
          <Slider
            aria-label="Speaker volume"
            value={volume}
            onChange={handleVolumeChange}
            min={0}
            max={100}
            step={5}
            valueLabelDisplay="off"
            sx={{ width: 120 }}
          />
        </Stack>
      </Popover>
    </>
  );
}
